/*
 * AnchorManager.js — Go AR · Core
 *
 * Quản lý tất cả anchors từ PRODUCTS config.
 * Mỗi anchor có ring + core Three.js và gọi callback khi found/lost.
 * Không biết gì về UI — chỉ emit events.
 */

import * as THREE from 'three';

export class AnchorManager {
  constructor(engine) {
    this.engine  = engine;
    this.anchors = new Map(); // anchorIndex → AnchorEntry
  }

  /*
   * register(sku, product, callbacks)
   * product.anchorIndex: number | number[] — mỗi index tạo 1 anchor,
   * tất cả cùng trỏ về 1 SKU (nhiều ảnh compile của cùng sản phẩm)
   */
  register(sku, product, { onFound, onLost } = {}) {
    /* Normalize: số → mảng 1 phần tử */
    const indexes = Array.isArray(product.anchorIndex)
      ? product.anchorIndex
      : [product.anchorIndex];

    const entries = indexes.map((anchorIndex) => {
      const anchor = this.engine.addAnchor(anchorIndex);

      /* ── Three.js visual: ring + core ── */
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.22, 0.022, 16, 48),
        new THREE.MeshBasicMaterial({
          color: 0x00c8bd,
          transparent: true,
          opacity: 0.9,
        })
      );
      anchor.group.add(ring);

      const core = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 20, 20),
        new THREE.MeshBasicMaterial({ color: 0x00c8bd })
      );
      anchor.group.add(core);

      const entry = { sku, product, anchor, ring, core, found: false };
      this.anchors.set(anchorIndex, entry);

      anchor.onTargetFound = () => {
        entry.found = true;
        /* Resolve config riêng theo anchor: product.anchors[index] override product */
        const resolved = product.anchors?.[anchorIndex]
          ? { ...product, ...product.anchors[anchorIndex] }
          : product;
        onFound?.(sku, resolved, anchor, anchorIndex);
      };

      anchor.onTargetLost = () => {
        entry.found = false;
        /* Chỉ báo lost khi TẤT CẢ anchor của SKU này đều lost */
        const stillFound = [...this.anchors.values()]
          .some(e => e.sku === sku && e.found);
        if (!stillFound) {
          onLost?.(sku, product, anchor, anchorIndex);
        }
      };

      return entry;
    });

    return entries;
  }

  /* Gọi mỗi frame để animate ring */
  update(t) {
    this.anchors.forEach((entry, i) => {
      const active = entry.found;
      const color  = active ? 0xff5c2e : 0x00c8bd;
      entry.ring.material.color.setHex(color);
      entry.core.material.color.setHex(color);
      entry.ring.scale.setScalar(
        1 + Math.sin(t * 3 + i) * (active ? 0.06 : 0.12)
      );
    });
  }

  getEntry(anchorIndex) {
    return this.anchors.get(anchorIndex);
  }

  getFoundSkus() {
    return [...this.anchors.values()]
      .filter(e => e.found)
      .map(e => e.sku);
  }
}
